import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Collapse, Drawer, Space, Spin } from 'antd';
import { useDataFilter } from '../../../hooks/SimViewer/useDataFilter';
import { useMetaData } from '../../../hooks/data/useMetaData';
import { useTripHistogramData } from '../../../hooks/data/useTripHistogramData';
import { downloadAsCsv } from '../../../utils/file';
import { toggleRange } from '../../../utils/range';
import { formatCount, formatTime } from '../../../utils/format';
import { toPascal } from '../../../utils/case';
import { HistogramChart } from '../Chart/HistogramChart';
import { FilterIcon } from '../../FilterIcon';
import { ActionButtons } from './ActionButtons';
import { HistogramComparison } from './HistogramComparison';

type Props = {
  icon: ReactNode;
  label: string;
  extra?: ReactNode;
};

const StayingPopulationPanel = ({
  icon,
  label,
  extra,
  ...props
}: Props): ReactNode => {
  const { dataFilter } = useDataFilter();

  return (
    <Collapse.Panel
      key="staying_population"
      header={
        <Space>
          {icon}
          {label}
          {extra}
          {dataFilter.stayingTime && <FilterIcon />}
        </Space>
      }
      {...props}
    >
      <StayingPopulationPanelContent label={label} />
    </Collapse.Panel>
  );
};

const StayingPopulationPanelContent = ({
  label,
}: Pick<Props, 'label'>): ReactNode => {
  const { t } = useTranslation();
  const { dataFilter, setRangeFilter } = useDataFilter();
  const { currentDataMeta } = useMetaData();
  const { isLoading, mean, median, items } =
    useTripHistogramData('staying_population');

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  return (
    <Spin spinning={isLoading}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space>
          <Text>
            {t('mean')}：
            {mean !== null ? formatCount(mean, t('unit.agent')) : '-'}
          </Text>
          <Text>
            {t('median')}：
            {median !== null ? formatCount(median, t('unit.agent')) : '-'}
          </Text>
        </Space>
        <HistogramChart
          data={items}
          rangeType="time"
          unit={t('unit.agent')}
          noDataMessage={t(
            'message.stayingPopulationUnavailableDueToConditions'
          )}
          hiddenFilter
          onBarClick={(clickedRange: [number, number]): void => {
            setRangeFilter(
              'stayingTime',
              toggleRange(clickedRange, dataFilter.stayingTime)
            );
          }}
        />
        <ActionButtons
          onOpenComparison={(): void => {
            setIsDrawerOpen(true);
          }}
          onExportCsv={(withBom: boolean): void => {
            downloadAsCsv(
              [
                [t('stayingTimeRange'), t('agentCount')],
                ...items.map((row): (string | number)[] => {
                  return [
                    `${formatTime(row.range[0])}~${formatTime(row.range[1])}`,
                    row.value,
                  ];
                }),
              ],
              `${currentDataMeta?.name ?? ''}_${toPascal(label)}.csv`,
              { withBom: withBom }
            );
          }}
          disabled={isLoading}
        />
        <Drawer
          title={t('barChartTitle', { target: label })}
          placement="right"
          width="80%"
          destroyOnClose
          open={isDrawerOpen}
          onClose={(): void => {
            setIsDrawerOpen(false);
          }}
        >
          <HistogramComparison
            type="staying_population"
            unit={t('unit.agent')}
            noDataMessage={t(
              'message.stayingPopulationUnavailableDueToConditions'
            )}
          />
        </Drawer>
      </Space>
    </Spin>
  );
};

const Text = styled.div`
  font-size: 12px;
`;

export { StayingPopulationPanel };
